const CONNECT_TIMEOUT_MS = 10000;
const RECONNECT_MAX_MS = 15000;
const RECONNECT_BASE_MS = 800;
const MAX_CAMERA_SUBSCRIPTIONS = 500;
const MAX_EVENT_MESSAGE_LENGTH = 512 * 1024;

const normalizeMacAddress = (value) =>
  String(value || "").replace(/\\/g, "").trim().toUpperCase();

const normalizeNamespace = (pathname) => {
  const clean = String(pathname || "/").trim();
  if (!clean || clean === "/") return "/";
  const withLeadingSlash = clean.startsWith("/") ? clean : `/${clean}`;
  return withLeadingSlash.replace(/\/+$/, "") || "/";
};

// The People Counter service exposes Socket.IO/Engine.IO directly on the full
// configured URL (for example https://sama-tech.ae/people-counter/api).
// IMPORTANT: this pathname is the Engine.IO transport endpoint itself, NOT a
// Socket.IO namespace. Do not rewrite it to /socket.io and do not append another
// path. The Socket.IO namespace remains the root namespace (/).
const parseSocketTarget = (serviceUrl) => {
  const parsed = new URL(String(serviceUrl || "").trim());
  if (!["http:", "https:", "ws:", "wss:"].includes(parsed.protocol)) {
    throw new Error("Unsupported People Counter realtime protocol.");
  }

  const securePage = typeof window !== "undefined" && window.location?.protocol === "https:";
  parsed.protocol = securePage || parsed.protocol === "https:" || parsed.protocol === "wss:" ? "wss:" : "ws:";
  parsed.username = "";
  parsed.password = "";
  parsed.pathname = parsed.pathname.replace(/\/+$/, "") || "/";
  parsed.search = "";
  parsed.hash = "";
  parsed.searchParams.set("EIO", "4");
  parsed.searchParams.set("transport", "websocket");

  return { websocketUrl: parsed.toString(), namespace: "/" };
};

const socketIoPacket = (type, namespace, payload = "") => {
  const ns = normalizeNamespace(namespace);
  return ns === "/" ? `${type}${payload}` : `${type}${ns},${payload}`;
};

const parseSocketIoPacket = (packet, namespace) => {
  if (!packet) return null;
  const type = packet[0];
  const rest = packet.slice(1);
  const ns = normalizeNamespace(namespace);
  if (ns === "/") return { type, payload: rest };
  const prefix = `${ns},`;
  if (!rest.startsWith(prefix)) return null;
  return { type, payload: rest.slice(prefix.length) };
};

const safeJsonParse = (value) => {
  try {
    return JSON.parse(value);
  } catch (_error) {
    return null;
  }
};

const uniqueMacAddresses = (values) => [
  ...new Set(
    (Array.isArray(values) ? values : [])
      .map(normalizeMacAddress)
      .filter(Boolean)
      .slice(0, MAX_CAMERA_SUBSCRIPTIONS)
  ),
];

/**
 * Minimal browser Socket.IO v4 client for the camera-service contract.
 *
 * The camera service only needs one namespace connection + JSON event emit/listen,
 * so keeping this adapter local avoids adding a large dependency to the IoT bundle.
 * It still speaks the documented Engine.IO v4 / Socket.IO framing over WebSocket.
 */
export class PeopleCounterRealtimeClient {
  constructor({ url, onStatus, onUpdate, onError }) {
    this.url = url;
    this.namespace = "/";
    this.onStatus = typeof onStatus === "function" ? onStatus : () => {};
    this.onUpdate = typeof onUpdate === "function" ? onUpdate : () => {};
    this.onError = typeof onError === "function" ? onError : () => {};

    this.socket = null;
    this.connected = false;
    this.destroyed = false;
    this.reconnectAttempt = 0;
    this.reconnectTimer = null;
    this.connectTimer = null;
    this.cameraMacs = [];
    this.cameraMacSet = new Set();
  }

  start(macAddresses = []) {
    this.destroyed = false;
    this.setCameras(macAddresses);
    if (!this.cameraMacs.length) {
      this.onStatus("idle");
      return;
    }
    this.connect();
  }

  setCameras(macAddresses = []) {
    const next = uniqueMacAddresses(macAddresses);
    const previous = this.cameraMacs;
    this.cameraMacs = next;
    this.cameraMacSet = new Set(next);

    if (!this.connected || !this.socket || this.socket.readyState !== WebSocket.OPEN) return;

    const previousSet = new Set(previous);
    const nextSet = new Set(next);
    const removed = previous.filter((mac) => !nextSet.has(mac));
    const added = next.filter((mac) => !previousSet.has(mac));

    if (removed.length) this.emit("unsubscribe_from_cameras", removed);
    if (added.length) this.emit("subscribe_to_cameras", added);
  }

  connect() {
    if (this.destroyed || !this.cameraMacs.length) return;
    if (this.socket && [WebSocket.CONNECTING, WebSocket.OPEN].includes(this.socket.readyState)) return;

    let socketUrl;
    try {
      const target = parseSocketTarget(this.url);
      socketUrl = target.websocketUrl;
      this.namespace = target.namespace;
    } catch (error) {
      this.onStatus("error");
      this.onError(error);
      return;
    }

    this.onStatus(this.reconnectAttempt > 0 ? "reconnecting" : "connecting");
    const socket = new WebSocket(socketUrl);
    this.socket = socket;
    this.connected = false;

    this.connectTimer = window.setTimeout(() => {
      if (this.socket !== socket || this.connected) return;
      try { socket.close(); } catch (_error) { /* noop */ }
    }, CONNECT_TIMEOUT_MS);

    socket.onmessage = (event) => {
      if (this.socket !== socket || typeof event.data !== "string") return;
      if (event.data.length > MAX_EVENT_MESSAGE_LENGTH) {
        this.onError(new Error("People Counter realtime event exceeded the allowed size."));
        return;
      }
      String(event.data)
        .split("\x1e")
        .filter(Boolean)
        .forEach((packet) => this.handlePacket(packet, socket));
    };

    socket.onerror = () => {
      if (this.socket !== socket || this.destroyed) return;
      this.onStatus("error");
    };

    socket.onclose = () => {
      if (this.socket !== socket) return;
      this.clearConnectTimer();
      this.socket = null;
      this.connected = false;
      if (this.destroyed || !this.cameraMacs.length) {
        this.onStatus("idle");
        return;
      }
      this.scheduleReconnect();
    };
  }

  handlePacket(packet, socket) {
    if (!packet || this.socket !== socket) return;

    // Engine.IO open packet. The configured URL is the transport endpoint itself;
    // connect to the root Socket.IO namespace.
    if (packet[0] === "0") {
      this.sendRaw(`4${socketIoPacket("0", this.namespace)}`);
      return;
    }

    // Engine.IO heartbeat. Echo any probe suffix as required by the protocol.
    if (packet[0] === "2") {
      this.sendRaw(`3${packet.slice(1)}`);
      return;
    }

    // Engine.IO close.
    if (packet[0] === "1") {
      try { socket.close(); } catch (_error) { /* noop */ }
      return;
    }

    // Socket.IO packets are Engine.IO message packets prefixed with "4".
    if (!packet.startsWith("4")) return;
    const decoded = parseSocketIoPacket(packet.slice(1), this.namespace);
    if (!decoded) return;

    // Namespace connected.
    if (decoded.type === "0") {
      this.clearConnectTimer();
      this.connected = true;
      this.reconnectAttempt = 0;
      this.onStatus("connected");
      if (this.cameraMacs.length) this.emit("subscribe_to_cameras", this.cameraMacs);
      return;
    }

    // Namespace disconnect/error.
    if (decoded.type === "1" || decoded.type === "4") {
      try { socket.close(); } catch (_error) { /* noop */ }
      return;
    }

    if (decoded.type !== "2") return;
    const eventData = safeJsonParse(decoded.payload);
    if (!Array.isArray(eventData) || eventData.length < 1) return;

    const [eventName, payload] = eventData;
    if (eventName !== "live_camera_update" || !payload || typeof payload !== "object") return;

    const eventMac = normalizeMacAddress(payload.macAddress || payload.mac_address);
    if (!eventMac || !this.cameraMacSet.has(eventMac)) return;
    this.onUpdate({ ...payload, macAddress: eventMac });
  }

  emit(eventName, payload) {
    if (!this.connected || !this.socket || this.socket.readyState !== WebSocket.OPEN) return false;
    return this.sendRaw(`4${socketIoPacket("2", this.namespace, JSON.stringify([eventName, payload]))}`);
  }

  sendRaw(packet) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return false;
    try {
      this.socket.send(packet);
      return true;
    } catch (error) {
      this.onError(error);
      return false;
    }
  }

  scheduleReconnect() {
    if (this.destroyed || this.reconnectTimer || !this.cameraMacs.length) return;
    this.reconnectAttempt += 1;
    this.onStatus("reconnecting");
    const exponential = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * (2 ** Math.min(this.reconnectAttempt - 1, 5)));
    const jitter = Math.round(Math.random() * Math.min(500, exponential * 0.2));
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, exponential + jitter);
  }

  clearConnectTimer() {
    if (!this.connectTimer) return;
    window.clearTimeout(this.connectTimer);
    this.connectTimer = null;
  }

  stop(notify = true) {
    this.destroyed = true;
    this.clearConnectTimer();
    if (this.reconnectTimer) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    const socket = this.socket;
    this.socket = null;
    if (socket && socket.readyState === WebSocket.OPEN) {
      // The camera-service docs require an explicit unsubscribe when leaving.
      if (this.connected && this.cameraMacs.length) {
        try {
          socket.send(`4${socketIoPacket("2", this.namespace, JSON.stringify(["unsubscribe_from_cameras", this.cameraMacs]))}`);
        } catch (_error) { /* noop */ }
      }
      try { socket.send(`4${socketIoPacket("1", this.namespace)}`); } catch (_error) { /* noop */ }
    }
    try { socket?.close(); } catch (_error) { /* noop */ }

    this.connected = false;
    if (notify) this.onStatus("idle");
  }
}

export const __test = {
  normalizeNamespace,
  parseSocketTarget,
  socketIoPacket,
  parseSocketIoPacket,
  uniqueMacAddresses,
};
